import { Box, Typography } from '@vp/swan';
import { __ } from '@99designs/i18n';
import {
    FieldViewSkinContext,
    FieldViewSkinType,
    useFieldViewSkin,
} from '../../../context/FieldViewContext';
import { Review as ClientConfiguredFieldReview } from '../FormFields/ClientConfiguredField';
import { Review as FilesFieldReview } from '../FormFields/FilesField';
import { Review as GridChoiceFieldReview } from '../FormFields/GridChoiceField/GridChoiceField';
import { IntelligentStylesReview } from '../FormFields/IntelligentStylesField/IntelligentStylesField';
import { Review as MultiGridChoiceFieldReview } from '../FormFields/MultiGridChoiceField/MultiGridChoiceField';
import { useIsReviewMode, useViewContext } from '../ViewContext';
import { FieldFragment } from '../brief.generated';
import FieldDisplay from './FieldDisplay';
import { isFieldShown } from './helpers';

interface FieldReviewProps {
    field: FieldFragment;
    skin?: FieldViewSkinType;
    colWidthOverride?: NonNullable<1 | 2 | 4 | 3 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12>;
}

export default function FieldReview({
    field,
    colWidthOverride,
    showTitle,
    ...props
}: FieldReviewProps & { showTitle?: boolean }) {
    const skin = useFieldViewSkin(props.skin);
    const viewContext = useViewContext();
    const fieldTitle = reviewFieldTitleWithOverrides(field);
    return isFieldShown(field, viewContext) ? (
        <Box mt="5">
            {showTitle && fieldTitle && (
                <Typography fontSkin="body-standard-bold" pb={'3'}>
                    {fieldTitle}
                </Typography>
            )}
            <FieldViewSkinContext.Provider value={skin}>
                <FieldReviewDisplay field={field} colWidthOverride={colWidthOverride} />
            </FieldViewSkinContext.Provider>
        </Box>
    ) : null;
}

interface FieldReviewDisplayProps {
    field: FieldFragment;
    colWidthOverride?: NonNullable<1 | 2 | 4 | 3 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12>;
}

export function FieldReviewDisplay({ field, colWidthOverride }: FieldReviewDisplayProps) {
    switch (field.__typename) {
        case 'GridChoiceField':
            return <GridChoiceFieldReview {...field} />;
        case 'MultiGridChoiceField':
            return <MultiGridChoiceFieldReview colWidthOverride={colWidthOverride} {...field} />;
        case 'FilesField':
            return <FilesFieldReview {...field} />;
        case 'IntelligentStylesField':
            return <IntelligentStylesReview {...field} />;
        case 'ClientConfiguredField':
            return <ClientConfiguredFieldReview {...field} />;
        default:
            return <FieldDisplay field={field} colWidthOverride={colWidthOverride} />;
    }
}

/**
 * TODO: replace with additional schema fields.
 */
export function reviewFieldTitleWithOverrides(field: FieldFragment): string {
    if (field.id === 'purpose') {
        return __('Use case');
    }

    return field.label;
}

export function useFieldTitleWithOverrides(field: FieldFragment): string {
    const isReviewMode = useIsReviewMode();

    return isReviewMode ? reviewFieldTitleWithOverrides(field) : field.label;
}
